import { RoleEnum } from '@/types/generated/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { cacheUpdateMemberRole } from '../member-handlers';
import { TUpdateMemberForm } from '../members.types';
import { useEditWorkspaceMember } from './use-update-members';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export const useUpdateMember = () => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [changeRoleData, setChangeRoleData] = useState<TUpdateMemberForm | null>();
  const { t } = useTranslation();
  const handleOpenEdit = () => setOpenEditModal(true);
  const handleCloseEdit = () => setOpenEditModal(false);

  const handleCloseChangeRoleModal = () => {
    setChangeRoleData(null);
  };

  const handleClose = () => {
    handleCloseChangeRoleModal();
    handleCloseEdit();
  };

  const updateFormSchema = (t: TFunction) => {
    return z.object({
      id: z.number().optional(),
      name: z.string().min(1, t('name_is_required')),
      email: z.string().min(1, t('email_is_required')).email(t('invalid_email_format')).toLowerCase(),
      role: z.string().min(1, t('role_is_required')),
    });
  };

  const updateForm = useForm<TUpdateMemberForm>({
    resolver: zodResolver(updateFormSchema(t)),
  });

  const { handleSubmit, control: updateMemberCotrol, reset } = updateForm;
  const { editMember, editMemberLoading } = useEditWorkspaceMember(cacheUpdateMemberRole, handleClose);

  const handleUpdateSubmit = handleSubmit(async function SubmitUpdateMember(data: TUpdateMemberForm) {
    if (!data?.id) return;

    editMember({
      variables: {
        input: {
          email: data?.email,
          name: data?.name,
          role: data?.role,
        },
        updateWorkspaceMemberV2Id: data?.id,
      },
    });
  });

  const handleRequestUpdateMember = (existingRecord: TUpdateMemberForm) => {
    reset({
      id: existingRecord.id,
      name: existingRecord.name,
      email: existingRecord.email,
      role: existingRecord.role.toLowerCase() as RoleEnum,
    });
    handleOpenEdit();
  };

  const handleCloseEditModal = handleCloseEdit;

  const handleMemberUpdate = ({ id, role, email, name }: TUpdateMemberForm) =>
    editMember({
      variables: {
        input: { role, name, email },
        updateWorkspaceMemberV2Id: id,
      },
    });

  const requestChangeRole = (roleData: TUpdateMemberForm) => {
    setChangeRoleData(roleData);
  };

  const handleChangeRole = () => {
    if (!changeRoleData?.id || !changeRoleData?.role) return;

    return handleMemberUpdate({ ...changeRoleData });
  };

  return {
    openEditModal,
    handleOpenEdit,
    handleCloseEditModal,
    updateMemberCotrol,
    handleRequestUpdateMember,
    editMemberLoading,
    handleUpdateSubmit,
    handleMemberUpdate,
    handleCloseChangeRoleModal,
    requestChangeRole,
    handleChangeRole,
    changeRoleData,
  };
};
